import { type FC, useCallback, useMemo, useState } from 'react';
import { Button, Radio, Spin, Switch } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import type { MetricColumnConfig } from '../../config/tpoGenConfig';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { useMetricApplicability } from '../../rules/store/useMetricApplicability';
import type { PPGRow } from '../../types/promoMetrics';
import KpiSelectDropdown from './components/KpiSelectDropdown';
import ProductSelectDropdown from './components/ProductSelectDropdown';
import PromoMetricsTable from './components/PromoMetricsTable';

type PromoMetricsView = 'promo-metrics' | 'promo-spends';

interface PromoMetricsSectionProps {
    ppgData: PPGRow[];
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    baseline: Map<string, Record<string, number>>;
    onAddProducts: (ppgIds: number[]) => void;
    isAddProductsDisabled: boolean;
    isLoadingRecommendedValues: boolean;
    isLoadingNextPage: boolean;
    hasMoreRecommendedValues: boolean | undefined;
    fetchNextRecommendedPage: () => void;
    hasProducts: boolean;
    promoType: string | undefined;
    instanceId: string;
    existingProductIds: number[];
    productOptions: { label: string; value: string }[];
    isLoadingProducts: boolean;
    onRefreshUplift: () => Promise<void>;
    isUpliftPending: boolean;
    isUpliftApplicable: boolean;
}

/**
 * Filters columns to the metrics applicable for the current cross-section state.
 * Applicability is engine-derived from `metricRules` (see `useMetricApplicability`):
 * `applicableByKey[key]` is present only for metrics that carry a rule, so a metric
 * without a rule is always applicable (`?? true`). Preserves the prior UX of
 * showing every metric until a promo type is chosen.
 */
function getApplicableConfigs(
    configs: MetricColumnConfig[],
    promoType: string | undefined,
    applicableByKey: Record<string, boolean>,
): MetricColumnConfig[] {
    if (!promoType) return configs;
    return configs.filter((c) => applicableByKey[c.key] ?? true);
}

function sortByOrder(configs: MetricColumnConfig[]): MetricColumnConfig[] {
    return [...configs].sort((a, b) => a.order - b.order);
}

/**
 * Visible columns are derived from the user's explicit customizations rather than
 * stored directly. A column is visible when it is applicable AND either default-visible
 * or explicitly added by the user, AND it has not been explicitly hidden by the user.
 */
function deriveVisibleConfigs(
    applicableConfigs: MetricColumnConfig[],
    hiddenKeys: Set<string>,
    extraKeys: Set<string>,
): MetricColumnConfig[] {
    return applicableConfigs.filter(
        (c) => (c.defaultVisible || extraKeys.has(c.key)) && !hiddenKeys.has(c.key),
    );
}

const PromoMetricsSection: FC<PromoMetricsSectionProps> = ({
    ppgData,
    onMetricChange,
    baseline,
    onAddProducts,
    isAddProductsDisabled,
    isLoadingRecommendedValues,
    isLoadingNextPage,
    hasMoreRecommendedValues,
    fetchNextRecommendedPage,
    hasProducts,
    promoType,
    instanceId,
    existingProductIds,
    productOptions,
    isLoadingProducts,
    onRefreshUplift,
    isUpliftPending,
    isUpliftApplicable,
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const applicableByKey = useMetricApplicability(instanceId);
    const [activePromoView, setActivePromoView] = useState<PromoMetricsView>('promo-metrics');
    const [isTreeView, setIsTreeView] = useState(false);
    // Track only explicit user customizations; visible columns are derived during render
    // so that newly-applicable default-visible columns (e.g. edlp_srp on merchandising)
    // always appear when the promo type changes, while honoring the user's choices.
    // hidden* = default-visible keys the user turned OFF.
    // extra*  = non-default-visible keys the user turned ON.
    const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => new Set());
    const [hiddenSpendKeys, setHiddenSpendKeys] = useState<Set<string>>(() => new Set());
    const [extraMetricKeys, setExtraMetricKeys] = useState<Set<string>>(() => new Set());
    const [extraSpendKeys, setExtraSpendKeys] = useState<Set<string>>(() => new Set());

    const applicableMetricConfigs = useMemo(
        () => sortByOrder(getApplicableConfigs(tpoGenConfig.promoMetricsConfig, promoType, applicableByKey)),
        [promoType, tpoGenConfig, applicableByKey],
    );

    const applicableSpendConfigs = useMemo(
        () => sortByOrder(getApplicableConfigs(tpoGenConfig.promoSpendsConfig, promoType, applicableByKey)),
        [promoType, tpoGenConfig, applicableByKey],
    );

    const visibleMetricConfigs = useMemo(
        () => deriveVisibleConfigs(applicableMetricConfigs, hiddenMetricKeys, extraMetricKeys),
        [applicableMetricConfigs, hiddenMetricKeys, extraMetricKeys],
    );

    const visibleSpendConfigs = useMemo(
        () => deriveVisibleConfigs(applicableSpendConfigs, hiddenSpendKeys, extraSpendKeys),
        [applicableSpendConfigs, hiddenSpendKeys, extraSpendKeys],
    );

    const isMetricsView = activePromoView === 'promo-metrics';
    const applicableConfigs = isMetricsView ? applicableMetricConfigs : applicableSpendConfigs;
    const visibleConfigs = isMetricsView ? visibleMetricConfigs : visibleSpendConfigs;

    const visibleKeys = useMemo(() => visibleConfigs.map((c) => c.key), [visibleConfigs]);

    const kpiSelectOptions = useMemo(
        () => applicableConfigs.map((c) => ({ label: c.title, value: c.key })),
        [applicableConfigs],
    );

    const handleVisibleKeysChange = useCallback(
        (selected: string[]) => {
            const selectedSet = new Set(selected);
            const setHidden = isMetricsView ? setHiddenMetricKeys : setHiddenSpendKeys;
            const setExtra = isMetricsView ? setExtraMetricKeys : setExtraSpendKeys;

            // Only the currently-applicable keys are offered in the dropdown, so we only
            // reconcile those; customizations for other promo types are left untouched.
            setHidden((prev) => {
                const next = new Set(prev);
                for (const c of applicableConfigs) {
                    if (!c.defaultVisible) continue;
                    if (selectedSet.has(c.key)) next.delete(c.key);
                    else next.add(c.key);
                }
                return next;
            });
            setExtra((prev) => {
                const next = new Set(prev);
                for (const c of applicableConfigs) {
                    if (c.defaultVisible) continue;
                    if (selectedSet.has(c.key)) next.add(c.key);
                    else next.delete(c.key);
                }
                return next;
            });
        },
        [applicableConfigs, isMetricsView],
    );

    return (
        <div>
            <div className="flex flex-row justify-between gap-x-4 mb-4">
                <Radio.Group defaultValue={'promo-metrics'}>
                    <Radio.Button value="promo-metrics" onClick={() => setActivePromoView('promo-metrics')}>
                        <p className={`text-sm ${activePromoView === 'promo-metrics' ? 'font-medium' : ''}`}>
                            {tpoGenConfig.promoMetricsGroupLabel}
                        </p>
                    </Radio.Button>
                    <Radio.Button value="promo-spends" onClick={() => setActivePromoView('promo-spends')}>
                        <p className={`text-sm ${activePromoView === 'promo-spends' ? 'font-medium' : ''}`}>
                            {tpoGenConfig.promoSpendsGroupLabel}
                        </p>
                    </Radio.Button>
                </Radio.Group>
                <div className="flex flex-row items-center gap-x-4">
                    {/* <div className="flex flex-row items-center gap-x-1">
                        <p className="text-[#52525C] text-xs">{'Tree View'}</p>
                        <Switch checked={!isTreeView} disabled onChange={(checked) => setIsTreeView(!checked)} />
                        <p className="text-[#52525C] text-xs">{'Flat View'}</p>
                    </div> */}
                    <div className="flex flex-row items-center gap-x-2">
                        <p className="text-[#52525C] text-xs">{'Total'}</p>
                        <Switch disabled />
                    </div>
                    {isUpliftApplicable && (
                        <span
                            className={`flex flex-row items-center gap-x-1 ${
                                isUpliftPending || !hasProducts
                                    ? 'pointer-events-none opacity-50 cursor-not-allowed'
                                    : 'cursor-pointer'
                            }`}
                            onClick={onRefreshUplift}
                        >
                            <ReloadOutlined spin={isUpliftPending} />
                            <p className="text-[#18181B] text-xs">{'Refresh Uplift'}</p>
                        </span>
                    )}
                    <KpiSelectDropdown
                        disabled={isAddProductsDisabled}
                        options={kpiSelectOptions}
                        value={visibleKeys}
                        onChange={handleVisibleKeysChange}
                    />
                    <ProductSelectDropdown
                        disabled={isAddProductsDisabled}
                        onApply={onAddProducts}
                        existingProductIds={existingProductIds}
                        options={productOptions}
                        isLoadingOptions={isLoadingProducts}
                    />
                </div>
            </div>

            {isLoadingRecommendedValues ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <Spin size="default" />
                    <p className="text-sm">Loading recommended values...</p>
                </div>
            ) : !hasProducts ? (
                <div className="flex flex-col items-center justify-center py-16 bg-gray-50">
                    <p className="text-sm text-gray-500 font-medium">Select products to view metrics</p>
                    <p className="text-xs text-gray-400 mt-1">{'Use the "Add Products" button above to get started'}</p>
                </div>
            ) : (
                <>
                    <PromoMetricsTable
                        metricConfigs={visibleConfigs}
                        isTreeView={isTreeView}
                        ppgData={ppgData}
                        onMetricChange={onMetricChange}
                        baseline={baseline}
                        showEditableIndicators={tpoGenConfig.showEditableIndicators}
                    />
                    {hasMoreRecommendedValues && (
                        <div className="flex justify-center items-center py-3">
                            {isLoadingNextPage ? (
                                <Spin size="small" />
                            ) : (
                                <Button type="link" size="small" onClick={fetchNextRecommendedPage}>
                                    Load more products
                                </Button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

PromoMetricsSection.displayName = 'PromoMetricsSection';

export default PromoMetricsSection;
